import { useState } from "react";

import { useNow } from "src/hooks/useNow";
import { completeEntry } from "src/api/client";
import { CancelEntryModal } from "src/components/CancelEntryModal";
import { SubmissionChecks } from "src/components/SubmissionChecks";
import { PatientIdCopy } from "src/components/PatientIdCopy";
import type { QueueEntry } from "src/api/types";

interface Props {
  entry: QueueEntry;
  token: string;
  isTaker: boolean;
  position: number;
  showSubmissions: boolean;
  onUpdated: (entry: QueueEntry) => void;
  onError: (message: string) => void;
}

export function QueueEntryCard({
  entry,
  token,
  isTaker,
  position,
  showSubmissions,
  onUpdated,
  onError,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  async function handleComplete() {
    setBusy(true);
    try {
      const updated = await completeEntry(token, entry.id);
      onUpdated(updated);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const now = useNow();
  const waitMins = Math.floor((now - new Date(entry.created_at).getTime()) / 60_000);
  const waitTime = waitMins < 1 ? "<1 min" : `${waitMins} min`;
  const sessionTime = entry.started_at ? formatDuration(entry.started_at, now) : null;

  return (
    <div className={`entry-card status-${entry.status}`}>
      <div className="entry-row">
        <span className="entry-position">{position}</span>

        <div className="entry-body">
          <div className="entry-main-row">
            {/* Left: ID with copy button + name column + status badge */}
            <div className="entry-left">
              {entry.patient_id ? (
                <PatientIdCopy patientId={entry.patient_id} />
              ) : (
                <span className="entry-id">—</span>
              )}
              {entry.patient_name && (
                <span className="entry-name">
                  {entry.patient_name}
                  {entry.patient_state && ` (${entry.patient_state})`}
                </span>
              )}
              <span className={`status-badge ${entry.status}`}>{statusLabel(entry.status)}</span>
              {showSubmissions && <SubmissionChecks entry={entry} />}
            </div>

            {/* Right: times */}
            <div className="entry-times">
              <span className="entry-time">{waitTime}</span>
              {entry.status === "waiting" && entry.estimated_wait_minutes != null && (
                <span className="entry-meta">
                  · Est: {Math.round(entry.estimated_wait_minutes)} min
                </span>
              )}
              {entry.status === "in_progress" && sessionTime && (
                <span className="entry-meta">· Session: {sessionTime}</span>
              )}
            </div>
          </div>

          {isTaker && (
            <div className="entry-actions">
              {entry.status === "in_progress" && (
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => void handleComplete()}
                  disabled={busy}
                >
                  Complete
                </button>
              )}
              {(entry.status === "waiting" || entry.status === "in_progress") && (
                <button
                  className="btn btn-sm btn-outline btn-danger"
                  onClick={() => setShowCancelModal(true)}
                  disabled={busy}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showCancelModal && (
        <CancelEntryModal
          entry={entry}
          token={token}
          onCancelled={onUpdated}
          onClose={() => setShowCancelModal(false)}
          onError={onError}
        />
      )}
    </div>
  );
}

function statusLabel(status: QueueEntry["status"]): string {
  switch (status) {
    case "waiting":
      return "Waiting";
    case "in_progress":
      return "In Progress";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
  }
}

function formatDuration(fromIso: string, now: number): string {
  const mins = Math.floor((now - new Date(fromIso).getTime()) / 60_000);
  if (mins < 1) return "<1 min";
  if (mins === 1) return "1 min";
  return `${mins} min`;
}
